import { ChangeEvent, FC, memo, useMemo } from "react";
import Roles from "../../../../models/roles";
import { useAppSelector } from "../../../../store";
import "./RadioInput.scss";

/**
 * Radio Props Interface
 * @interface RadioProps
 */
interface RadioProps {
  /**  */
  label: string;
  /** Name of the radio group */
  name: string;
  /** Currently selected value */
  value?: string | number;
  /** Handler for value changes */
  onChange: (value: string) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Error message if validation fails */
  error?: string;
  /** Whether the field has been touched */
  touched?: boolean;
  /**  */
  checked?: boolean;
  /**  */
  disabled?: boolean;
  /**  */
  themeCheck?: boolean;
  checkedAlert?: boolean;
}

/**
 * Simple Radio Component
 * A reusable radio button group component with proper accessibility
 * and validation support.
 */
const SimpleRadio: FC<RadioProps> = ({
  label,
  name,
  value,
  disabled = false,
  checked,
  onChange,
  required = false,
  className = "",
  error,
  touched,
  themeCheck = false,
  checkedAlert = false,
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    onChange(value);
  };

  const user = useAppSelector((state) => state.auth.user)

  const groupId = `radio-group-${name}`;
  const errorId = `${groupId}-error`;

  const isSuperAdmin = useMemo(() => user?.role.name === Roles.SUPER_ADMIN, [user])
  const isCentreAdmin = useMemo(() => user?.role.name === Roles.CENTER_ADMIN, [user])

  return themeCheck === false ? (
    <div
      className={`radio-group ${className} ${
        touched && error ? "has-error" : ""
      }`.trim()}
      role="radiogroup"
      aria-required={required}
      aria-invalid={touched && error ? "true" : "false"}
      aria-describedby={error ? errorId : undefined}
    >
      <div className="radio-input">
        <label htmlFor={label} className="radio-input__label">
          <input
            type="radio"
            id={label}
            name={name}
            value={value}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            required={required}
            className="radio-input__input"
            aria-describedby={error ? errorId : undefined}
          />
          <div className="checkmark" style={{ background: value }} />
          {label}
          {(label === "Centre Admin" && isSuperAdmin) && (
            <span className="fs-13 text-grey-medium ps-10px">
              Full access to all centres and all functions
            </span>
          )}
          {(label === "Centre Admin" && isCentreAdmin) && (
            <span className="fs-13 text-grey-medium ps-10px">
              Full access to all centres and all functions
            </span>
          )}
          {(label === "Centre User" && isSuperAdmin) && (
            <span className="fs-13 text-grey-medium ps-10px">
              Access to selected or all centres, and cannot edit centres/users
            </span>
          )}
          {(label === "Centre User" && isCentreAdmin) && (
            <span className="fs-13 text-grey-medium ps-10px">
              Access to selected or all centres, and cannot edit centres/users
            </span>
          )}
          {label === "Super Admin" && (
            <span className="fs-13 text-grey-medium ps-10px">
              Ability to create, edit, deactivate other Super Admin and General Admin
            </span>
          )}
          {required && (
            <span className="required-asterisk" aria-hidden="true">
              *
            </span>
          )}
        </label>
      </div>

      {touched && error && (
        <div id={errorId} className="radio-error" role="alert">
          {error}
        </div>
      )}
    </div>
  ) : (
    <>
      <input
        type="radio"
        className="theme-check-input"
        id={label}
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        required={required}
      />
      <label
        htmlFor={label}
        className={`theme-check-label fs-16 fw-400 ${
          checkedAlert ? "checked-alert" : ""
        }`}
      >
        {label}
        {(label === "Centre Admin") && (
            <span className="fs-13 text-grey-medium ps-10px">
              Full access to all centres and all functions
            </span>
          )}
         
          {(label === "Centre User") && (
            <span className="fs-13 text-grey-medium ps-10px">
              Access to selected or all centres, and cannot edit centres/users
            </span>
          )}
          {label === "Super Admin" && (
            <span className="fs-13 text-grey-medium ps-10px">
              Ability to create, edit, deactivate other Super Admin and General Admin
            </span>
          )}
          {required && (
            <span className="required-asterisk" aria-hidden="true">
              *
            </span>
          )}
      </label>
    </>
  );
};

export default memo(SimpleRadio);
